/**
 * Git Credentials Section Component
 *
 * Displays per-server git auth state and lets users either connect an
 * OAuth login or configure a personal access token, depending on how the
 * server is set up. Per-row rendering lives in `GitServerAuthRow` to keep
 * this component under the 200-line cap.
 */

import type { ReactElement } from 'react';
import { useGitCredentials } from '../../git/hooks/useGitCredentials';
import { GitServerAuthRow } from '../../git/components/GitServerAuthRow';

export function GitCredentialsSection(): ReactElement {
  const { data, isLoading } = useGitCredentials();

  if (isLoading) {
    return (
      <section className="settings-section">
        <h2>Git Credentials</h2>
        <div className="settings-loading">Loading credentials…</div>
      </section>
    );
  }

  const creds = data?.credentials ?? [];

  if (creds.length === 0) {
    return (
      <section className="settings-section">
        <h2>Git Credentials</h2>
        <p className="section-description">
          No git servers are configured. Ask an administrator to register one.
        </p>
      </section>
    );
  }

  return (
    <section className="settings-section">
      <h2>Git Credentials</h2>
      <p className="section-description">
        Connect a login or configure a personal access token for each git server.
      </p>

      <ul className="git-credentials-list">
        {creds.map((c) => (
          <GitServerAuthRow key={c.server_id} credential={c} />
        ))}
      </ul>
    </section>
  );
}
